package com.hyperether.goodjob.navigation

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.Icon
import androidx.compose.material.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.text.font.FontWeight
import androidx.navigation.NavHostController
import androidx.navigation.compose.currentBackStackEntryAsState
import com.hyperether.goodjob.repository.prefs.PrefsManager
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.briefcase
import com.hyperether.goodjob.resources.briefcase_unselect
import com.hyperether.goodjob.resources.calendar
import com.hyperether.goodjob.resources.calendar_unselect
import com.hyperether.goodjob.resources.contacts
import com.hyperether.goodjob.resources.dahboard
import com.hyperether.goodjob.resources.dahboard_unselect
import com.hyperether.goodjob.resources.dashboard
import com.hyperether.goodjob.resources.employees
import com.hyperether.goodjob.resources.employees_unselect
import com.hyperether.goodjob.resources.ic_logout
import com.hyperether.goodjob.resources.ic_logout_selected
import com.hyperether.goodjob.resources.ic_settings
import com.hyperether.goodjob.resources.ic_settings_selected
import com.hyperether.goodjob.resources.jobs
import com.hyperether.goodjob.resources.logout
import com.hyperether.goodjob.resources.map
import com.hyperether.goodjob.resources.map_unselect
import com.hyperether.goodjob.resources.places
import com.hyperether.goodjob.resources.scheduler
import com.hyperether.goodjob.resources.settings
import com.hyperether.goodjob.resources.user_check
import com.hyperether.goodjob.resources.user_check_unselect
import com.hyperether.goodjob.scenes.components.CircleWithInitialUser
import com.hyperether.goodjob.scenes.components.clickableWithPointer
import com.hyperether.goodjob.scenes.components.disableMouseDragScrollOnWeb
import com.hyperether.goodjob.scenes.workspace.WorkspaceViewModel
import com.hyperether.goodjob.theme.PricingText
import com.hyperether.goodjob.theme.SelectedIconTint
import com.hyperether.goodjob.theme.SplashBg
import com.hyperether.goodjob.theme.UnselectedIconTint
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.goodjob.util.toScaleSp
import com.hyperether.resources.stringResource
import org.jetbrains.compose.resources.painterResource

@Composable
fun WebNav(
    navController: NavHostController,
    workspaceViewModel: WorkspaceViewModel,
    prefsManager: PrefsManager,
    onLogout: () -> Unit
) {
    val navBackStackEntry by navController.currentBackStackEntryAsState()
    val currentDestination = navBackStackEntry?.destination

    val user by prefsManager.userFlow.collectAsState()
    val isEmployee = user?.role?.equals("employee", ignoreCase = true) == true
    val navItems = getWebNavItemsList(isEmployee, onLogout)
    val currentIndex = getCurrentIndex(navItems, currentDestination?.route, 0)
    val lastSelectedIndex = rememberPersistedIntState("lastSelectedIndex", currentIndex)
    var selectedWebNavItemName by remember { mutableStateOf(navItems[currentIndex].route) }

    LaunchedEffect(currentDestination) {
        currentDestination?.route?.let { route ->
            val newIndex = getCurrentIndex(navItems, route, lastSelectedIndex.value)
            lastSelectedIndex.value = newIndex

            selectedWebNavItemName = navItems[newIndex].route
        }
    }

    val selectedWorkspace = remember { mutableStateOf<String?>(null) }
    val workspaces = workspaceViewModel.workspaceList

    LaunchedEffect(workspaces) {
        if (workspaces.isNotEmpty()) {
            selectedWorkspace.value = workspaces.first().name
        }
    }

    Column(
        modifier = Modifier
            .fillMaxHeight()
            .width(310.toScaleDp())
            .background(SplashBg)
            .verticalScroll(rememberScrollState())
            .disableMouseDragScrollOnWeb(),
        verticalArrangement = Arrangement.SpaceBetween
    ) {
        // Profile Section
        Column(
            modifier = Modifier.padding(24.toScaleDp()),
            horizontalAlignment = Alignment.Start
        ) {
            Row {
                CircleWithInitialUser(user, size = 56.toScaleDp())
                Spacer(modifier = Modifier.width(12.toScaleDp()))
                Column {
                    Text(
                        text = user?.getName() ?: "",
                        fontWeight = FontWeight.W700,
                        color = PricingText,
                        fontSize = 18.toScaleSp()
                    )
                    Text(
                        text = user?.email ?: "",
                        color = UnselectedIconTint,
                        fontSize = 13.toScaleSp(),
                        fontWeight = FontWeight.W400
                    )
                }
            }
            Spacer(modifier = Modifier.height(44.toScaleDp()))
            // TODO: uncomment when we get support for multiple workspaces
//            WorkspaceTypeDropdown(
//                selectedType = selectedWorkspace.value ?: "",
//                onTypeSelected = { selected ->
//                    selectedWorkspace.value = selected
//                    expandedDropdown.value = false
//                },
//                expanded = expandedDropdown.value,
//                onExpandToggle = { expandedDropdown.value = it },
//                modifier = Modifier.width(250.toScaleDp()),
//                workspaces = workspaces
//            )
        }

        val endIndex = navItems.size // Always the last item

        // Navigation Items
        Column(
            modifier = Modifier.padding(horizontal = 24.toScaleDp()),
            verticalArrangement = Arrangement.spacedBy(12.toScaleDp())
        ) {
            navItems.subList(0, endIndex - 2).forEachIndexed { index, item ->
                WebDrawerItem(
                    isSelected = selectedWebNavItemName == item.route,
                    item = item,
                    onClick = {
                        navController.navigate(item.route)
                        item.onClick?.invoke()
                    }
                )
            }
        }

        // Spacer between main items and bottom items
        Spacer(modifier = Modifier.weight(1f))

        // Settings and Logout
        Column(
            modifier = Modifier.padding(horizontal = 24.toScaleDp()),
            verticalArrangement = Arrangement.spacedBy(12.toScaleDp())
        ) {
            navItems.subList(endIndex - 2, endIndex).forEachIndexed { index, item ->
                val actualIndex = index + endIndex

                WebDrawerItem(
                    isSelected = selectedWebNavItemName == item.route,
                    item = item,
                    onClick = {
                        navController.navigate(item.route)
                        item.onClick?.invoke()
                    }
                )
            }
            Spacer(modifier = Modifier.height(32.toScaleDp()))
        }
    }
}

data class WebNavItem(
    val name: String,
    val iconResId: Painter,
    val unselectedIconResId: Painter,
    val route: RouteScreen,
    val onClick: (() -> Unit)? = null
)

@Composable
fun WebDrawerItem(
    isSelected: Boolean,
    item: WebNavItem,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(56.toScaleDp())
            .clip(RoundedCornerShape(10.toScaleDp()))
            .background(
                color = if (isSelected) {
                    Color.White.copy(alpha = 0.08f)
                } else {
                    Color.Transparent
                }
            )
            .clickableWithPointer {
                onClick()
            }
            .padding(10.toScaleDp()),
        contentAlignment = Alignment.CenterStart
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 10.toScaleDp()),
            horizontalArrangement = Arrangement.Start,
            verticalAlignment = Alignment.CenterVertically
        ) {
            val icon = if (isSelected) item.iconResId else item.unselectedIconResId
            Icon(
                modifier = Modifier.size(24.toScaleDp()),
                painter = icon,
                contentDescription = null,
                tint = if (isSelected) SelectedIconTint else UnselectedIconTint
            )
            Spacer(modifier = Modifier.width(16.toScaleDp()))
            Text(
                text = item.name,
                fontSize = 16.toScaleSp(),
                fontWeight = if (isSelected) FontWeight.W700 else FontWeight.W400,
                color = if (isSelected) SelectedIconTint else UnselectedIconTint
            )
        }
    }
}

@Composable
fun getWebNavItemsList(isEmployee: Boolean, onLogout: () -> Unit): List<WebNavItem> {
    return if (!isEmployee) {
        listOf(
            WebNavItem(
                name = stringResource(Res.string.dashboard),
                iconResId = painterResource(Res.drawable.dahboard),
                unselectedIconResId = painterResource(
                    Res.drawable.dahboard_unselect
                ),
                route = Dashboard
            ),
            WebNavItem(
                name = stringResource(Res.string.employees),
                iconResId = painterResource(Res.drawable.employees),
                unselectedIconResId = painterResource(
                    Res.drawable.employees_unselect
                ),
                route = Users
            ),
            WebNavItem(
                name = stringResource(Res.string.jobs),
                iconResId = painterResource(Res.drawable.briefcase),
                unselectedIconResId = painterResource(
                    Res.drawable.briefcase_unselect
                ),
                route = Jobs
            ),
            WebNavItem(
                name = stringResource(Res.string.scheduler),
                iconResId = painterResource(Res.drawable.calendar),
                unselectedIconResId = painterResource(
                    Res.drawable.calendar_unselect
                ),
                route = Scheduler
            ),
            WebNavItem(
                name = stringResource(Res.string.contacts),
                iconResId = painterResource(Res.drawable.user_check),
                unselectedIconResId = painterResource(
                    Res.drawable.user_check_unselect
                ),
                route = Contacts
            ),
            WebNavItem(
                name = stringResource(Res.string.places),
                iconResId = painterResource(Res.drawable.map),
                unselectedIconResId = painterResource(
                    Res.drawable.map_unselect
                ),
                route = Map
            ),
            WebNavItem(
                name = stringResource(Res.string.settings),
                iconResId = painterResource(Res.drawable.ic_settings_selected),
                unselectedIconResId = painterResource(
                    Res.drawable.ic_settings
                ),
                route = SettingsWeb
            ),
            WebNavItem(
                name = stringResource(Res.string.logout),
                iconResId = painterResource(Res.drawable.ic_logout_selected),
                unselectedIconResId = painterResource(
                    Res.drawable.ic_logout
                ),
                route = Landing,
                onClick = {
                    onLogout()
                }
            )
        )
    } else {
        listOf(
            WebNavItem(
                name = stringResource(Res.string.dashboard),
                iconResId = painterResource(Res.drawable.dahboard),
                unselectedIconResId = painterResource(
                    Res.drawable.dahboard_unselect
                ),
                route = Dashboard
            ),
            WebNavItem(
                name = stringResource(Res.string.jobs),
                iconResId = painterResource(Res.drawable.briefcase),
                unselectedIconResId = painterResource(
                    Res.drawable.briefcase_unselect
                ),
                route = Jobs
            ),
            WebNavItem(
                name = stringResource(Res.string.scheduler),
                iconResId = painterResource(Res.drawable.calendar),
                unselectedIconResId = painterResource(
                    Res.drawable.calendar_unselect
                ),
                route = Scheduler
            ),
            WebNavItem(
                name = stringResource(Res.string.places),
                iconResId = painterResource(Res.drawable.map),
                unselectedIconResId = painterResource(
                    Res.drawable.map_unselect
                ),
                route = Map
            ),
            WebNavItem(
                name = stringResource(Res.string.settings),
                iconResId = painterResource(Res.drawable.ic_settings_selected),
                unselectedIconResId = painterResource(
                    Res.drawable.ic_settings
                ),
                route = SettingsWeb
            ),
            WebNavItem(
                name = stringResource(Res.string.logout),
                iconResId = painterResource(Res.drawable.ic_logout_selected),
                unselectedIconResId = painterResource(
                    Res.drawable.ic_logout
                ),
                route = Landing,
                onClick = {
                    onLogout()
                }
            )
        )
    }
}

fun getCurrentIndex(
    navList: List<WebNavItem>,
    currentDestination: String?,
    lastSelectedIndex: Int
): Int {
    val endIndex = navList.size
    val splitIndex = endIndex - 2

    navList.subList(0, splitIndex).forEachIndexed { index, item ->
        if (item.route.route == currentDestination) {
            return index
        }
    }

    navList.subList(splitIndex, endIndex).forEachIndexed { index, item ->
        if (item.route.route == currentDestination) {
            return splitIndex + index
        }
    }

    return lastSelectedIndex
}